import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, Clock, CheckCircle, ArrowRight, Euro, MapPin } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';
import { leistungenData } from '../data/leistungenData';

const LeistungenPage = () => {
  const phone = '+4923139085116';
  const displayPhone = '0231 39085116';

  return (
    <>
      <Helmet>
        <title>Schlüsseldienst Dortmund – Unsere Leistungen rund um Tür, Auto & Sicherheit</title>
        <meta name="description" content="Übersicht aller Leistungen vom Schlüsseldienst Dortmund: Türöffnungen, Autoöffnung, Tresoröffnung, Schlosswechsel & Einbruchschutz. Schnelle Hilfe zum Festpreis." />
        <link rel="canonical" href="https://schluesseldienst.services/leistungen/" />
      </Helmet>

      <div className="container mx-auto px-4 py-12">
        <Breadcrumbs items={[{ label: 'Leistungen' }]} />
        
        <h1 className="text-4xl sm:text-5xl font-bold mb-8 text-gray-900">
          Unsere Leistungen als Schlüsseldienst in Dortmund
        </h1>

        {/* Einleitung */}
        <div className="max-w-4xl mb-12">
          <p className="text-lg text-gray-700 mb-4">
            Als erfahrener Schlüsseldienst in Dortmund bieten wir Ihnen ein umfassendes Leistungsspektrum – 
            von der schnellen Türöffnung über Autoöffnung bis hin zu Sicherheitsberatung und Einbruchschutz.
          </p>
          <p className="text-lg text-gray-700 mb-4">
            Die Schössow Dienstleistungen UG steht für faire Preise, transparente Kostenabsprachen und 
            professionelle Arbeit. Wir sind 24 Stunden am Tag, 7 Tage die Woche für Sie da – auch an 
            Sonn- und Feiertagen.
          </p>
        </div>

        {/* Schnelle Hilfe Box */}
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 mb-16 border-2 border-orange-200">
          <div className="flex items-center gap-3 mb-6">
            <Clock className="w-8 h-8 text-orange-600" />
            <h2 className="text-3xl font-bold text-gray-900">Schnelle Hilfe zum Festpreis</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <h3 className="font-bold text-lg">24/7 Notdienst</h3>
              </div>
              <p className="text-gray-700">
                Rund um die Uhr erreichbar – auch nachts, am Wochenende und an Feiertagen
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="flex items-center gap-2 mb-3">
                <Euro className="w-5 h-5 text-green-600" />
                <h3 className="font-bold text-lg">Transparente Festpreise</h3>
              </div>
              <p className="text-gray-700 mb-2">
                <strong>Mo–Fr 08–20 Uhr: 119 €</strong><br />
                <strong>Sa–So: 199 €</strong>
              </p>
              <p className="text-sm text-gray-600">Anfahrt inklusive, keine versteckten Kosten</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-5 h-5 text-green-600" />
                <h3 className="font-bold text-lg">Ganz Dortmund</h3>
              </div>
              <p className="text-gray-700">
                In allen Stadtteilen für Sie im Einsatz – schnelle Anfahrt garantiert
              </p>
            </div>
          </div>

          <div className="text-center">
            <a
              href={`tel:${phone}`}
              className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-full font-bold text-xl transition-all shadow-lg hover:shadow-xl"
            >
              <Phone size={24} />
              {displayPhone}
            </a>
            <p className="text-gray-700 mt-3">Sofort anrufen – wir sind für Sie da!</p>
          </div>
        </div>

        {/* Leistungen Grid */}
        <h2 className="text-3xl font-bold mb-8 text-gray-900">Alle Leistungen im Überblick</h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {leistungenData.map((leistung) => (
            <div 
              key={leistung.id}
              className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 hover:border-orange-300 hover:shadow-xl transition-all"
            >
              <div className="text-4xl mb-4">{leistung.icon}</div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">{leistung.title}</h3>
              <p className="text-gray-700 mb-6">
                {leistung.shortDescription}
              </p>
              <Link
                to={`/leistungen/${leistung.slug}`}
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full font-semibold transition-all"
              >
                Mehr erfahren
                <ArrowRight size={18} />
              </Link>
            </div>
          ))}
        </div>

        {/* CTA Footer */}
        <div className="bg-gray-900 text-white rounded-2xl p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Jetzt Kontakt aufnehmen</h2>
          <p className="text-lg mb-6 text-gray-300">
            Haben Sie Fragen zu unseren Leistungen? Benötigen Sie sofort Hilfe?<br />
            Rufen Sie uns an – wir beraten Sie gerne!
          </p>
          <a
            href={`tel:${phone}`}
            className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-full font-bold text-xl transition-all shadow-lg hover:shadow-xl"
          >
            <Phone size={24} />
            {displayPhone}
          </a>
          <p className="text-gray-400 mt-4">24/7 erreichbar • Festpreise • Schnelle Anfahrt</p>
        </div>
      </div>
    </>
  );
};

export default LeistungenPage;
